import { useEffect, useState } from "react";
import {
  api_private_get,
  api_private_post,
  showAlert,
  formatStringAmountCredits,
} from "script";
import useAuth from "context/useAuth";

import WithdrawTermsModal from "../modals/WithdrawTermsModal";
import WithdrawConfirmationModal from "../modals/WithdrawConfirmationModal";

// Withdrawal eligibility requirements.
const MIN_TRADES = 5;
const MIN_TRADED_AMOUNT = 20;

function formatCadAmount(amount) {
  return `${new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0)} CAD`;
}

export default function WithdrawTab({ setActiveTab }) {
  const { user, refreshUser } = useAuth();

  const currency = "cad";
  const method = "interac";

  const fullName = user?.display_name || "";
  const email = user?.email || "";

  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [showModalWithdrawTerms, setShowModalWithdrawTerms] = useState(false);
  const [showModalWithdrawConfirmation, setShowModalWithdrawSubmit] =
    useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isRefreshingBalance, setIsRefreshingBalance] = useState(false);
  const [isSubmittingWithdrawal, setIsSubmittingWithdrawal] = useState(false);

  // Trade-based withdrawal eligibility.
  const [tradeCount, setTradeCount] = useState(0);
  const [totalTraded, setTotalTraded] = useState(0);

  useEffect(() => {
    api_private_get("/account/get_orders", (data) => {
      const orders = Array.isArray(data) ? data : [];
      setTradeCount(orders.length);
      const total = orders.reduce(
        (sum, o) => sum + (parseFloat(o.order_amount) || 0),
        0,
      );
      setTotalTraded(total);
    });
  }, []);

  const meetsTradeCount = tradeCount >= MIN_TRADES;
  const meetsTradedAmount = totalTraded >= MIN_TRADED_AMOUNT;
  const meetsWithdrawRequirements = meetsTradeCount && meetsTradedAmount;

  // Numeric value of the entered amount (strips the display formatting).
  const numericAmount = parseFloat(withdrawAmount.replace(/,/g, "")) || 0;

  // Refresh the balance while keeping the spinner visible for at least one full
  // rotation, even though the fetch resolves almost instantly.
  const refreshBalance_onClick = () => {
    if (isRefreshingBalance) return;
    setIsRefreshingBalance(true);
    Promise.all([
      Promise.resolve(refreshUser()),
      new Promise((resolve) => setTimeout(resolve, 750)),
    ]).finally(() => setIsRefreshingBalance(false));
  };

  // Format the entered amount with thousand separators and up to 2 decimals
  // while keeping the raw digits the user typed.
  const setWithdrawAmountFn = (value) => {
    let cleaned = value.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    const hasDot = parts.length > 1;
    const intPart = parts[0].replace(/^0+(?=\d)/, "");
    const decPart = hasDot ? parts.slice(1).join("").slice(0, 2) : "";
    const intFormatted =
      intPart === ""
        ? hasDot
          ? "0"
          : ""
        : Number(intPart).toLocaleString("en-US");
    setWithdrawAmount(hasDot ? `${intFormatted}.${decPart}` : intFormatted);
  };

  const formatWithdrawAmountOnBlur = () => {
    setWithdrawAmount(
      numericAmount > 0
        ? numericAmount.toLocaleString("en-CA", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : "",
    );
  };

  const handleModalWithdrawTermsClose = (confirmed) => {
    setShowModalWithdrawTerms(false);
    if (confirmed) {
      setTermsAccepted(true);
    }
  };

  const handleModalWithdrawConfirmationClose = (confirmed) => {
    if (isSubmittingWithdrawal) return;

    if (!confirmed) {
      setShowModalWithdrawSubmit(false);
      return;
    }

    if (confirmed) {
      setIsSubmittingWithdrawal(true);
      api_private_post(
        "/wallet/withdraw",
        {
          cad_amount: numericAmount,
          currency: currency,
          method: method,
          address: email,
        },
        (data) => {
          if (!data.success) {
            console.error("Withdrawal failed:", data);
            showAlert(`Withdrawal request failed: ${data.error}`, "danger");
            setIsSubmittingWithdrawal(false);
            return;
          }
          setIsSubmittingWithdrawal(false);
          setShowModalWithdrawSubmit(false);
          showAlert(
            `Withdrawal of ${formatCadAmount(numericAmount)} by Interac e-Transfer to ${email} has been submitted.`,
            "success",
          );
          refreshUser();
          setTimeout(() => {
            setActiveTab("balance-history");
            window.scrollTo({ top: 0, behavior: "smooth" });
          }, 500);
        },
        (error) => {
          console.error("Withdrawal error:", error);
          showAlert("Withdrawal request failed. Please try again.", "danger");
          setIsSubmittingWithdrawal(false);
        },
      );

      // Future USD withdrawal payload, once USD balances and payout
      // methods are supported:
      // api_private_post('/wallet/withdraw', { usd_amount: numericAmount, currency: 'usd', method, address: email }, ...);
    }
  };

  const handleWithdrawSubmitBtn = (e) => {
    e.preventDefault();
    if (!meetsWithdrawRequirements) {
      showAlert(
        `You must place at least ${MIN_TRADES} trades totaling at least $${MIN_TRADED_AMOUNT} before you can withdraw.`,
        "danger",
      );
      return;
    }
    if (numericAmount <= 0) {
      showAlert("Please enter a valid withdrawal amount.", "danger");
      return;
    }
    setShowModalWithdrawSubmit(true);
  };

  return (
    <div id="withdraw-tab">
      <WithdrawTermsModal
        show={showModalWithdrawTerms}
        onClose={handleModalWithdrawTermsClose}
      />
      <WithdrawConfirmationModal
        show={showModalWithdrawConfirmation}
        onClose={handleModalWithdrawConfirmationClose}
        withdrawAmtRequested={withdrawAmount}
        currency={currency}
        method={method}
        withdrawAddress={email}
        isSubmitting={isSubmittingWithdrawal}
      />

      <div className="pay_method__paymethod p-4 p-lg-6 p2-bg rounded-8 mb-8 mb-md-10">
        <div className="pay_method__paymethod-title d-flex align-items-center gap-3 mb-6 mb-md-8">
          <i className="ti ti-credit-card fs-four g1-color"></i>
          <h5 className="n10-color">Payment method</h5>
        </div>
        <div className="pay_method__paymethod-alitem">
          <div className="row gx-4 gy-4">
            <div className="col-6 col-sm-4 col-md-3 col-lg-4 col-xl-3 col-xxl-2">
              <div
                id="withdraw-select-interac"
                className="pay_method__paymethod-item pay-methods p-2 rounded-3"
              >
                <img
                  src="/assets/images/payments/interac.png"
                  alt="interac"
                  className="rounded"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="pay_method__paymethod p-4 p-lg-6 p2-bg rounded-8">
        <div className="pay_method__amount d-flex align-content-center justify-content-between py-3 px-5 px-md-6 mb-6 mb-md-8 flex-wrap gap-3">
          <div className="pay_method__amount-actual">
            <span className="fs-seven mb-3">Actual balance</span>
            <div className="d-flex align-items-center gap-3">
              <span className="fw-bol" id="withdraw-refresh-balance-text">
                {formatStringAmountCredits(user?.balance)}
              </span>
              <i
                className="ti ti-refresh fs-seven cpoint"
                id="withdraw-refresh-balance-btn"
                onClick={refreshBalance_onClick}
                style={{
                  display: "inline-block",
                  animation: isRefreshingBalance
                    ? "spin 0.75s linear infinite"
                    : "none",
                }}
              ></i>
            </div>
          </div>
        </div>

        <div className="pay_method__paymethod-title mb-5 mb-md-6">
          <h5 className="n10-color">Interac e-Transfer recipient</h5>
        </div>

        <div className="container">
          <form id="withdraw-form" onSubmit={handleWithdrawSubmitBtn}>
            {/* Registered full name (auto-filled, read-only) */}
            <div className="mb-5">
              <label
                htmlFor="withdraw-recipient-name"
                className="fs-seven mb-2 d-block"
              >
                Registered full name
              </label>
              <input
                className="n11-bg p-5 fw-bold w-100"
                type="text"
                id="withdraw-recipient-name"
                name="recipient-name"
                value={fullName}
                readOnly
                disabled
              />
            </div>

            {/* Registered email (auto-filled, read-only) — funds are sent here */}
            <div className="mb-5">
              <label
                htmlFor="withdraw-recipient-email"
                className="fs-seven mb-2 d-block"
              >
                Interac e-Transfer email
              </label>
              <input
                className="n11-bg p-5 fw-bold w-100"
                type="text"
                id="withdraw-recipient-email"
                name="recipient-email"
                value={email}
                readOnly
                disabled
              />
            </div>

            <div className="pay_method__paymethod-title mb-5 mb-md-6">
              <h5 className="n10-color">Enter your withdrawal amount (CAD)</h5>
            </div>
            <div className="mb-5">
              <div className="n11-bg rounded-3 d-flex align-items-center px-5">
                <span className="fw-bold" aria-hidden="true">
                  $
                </span>
                <input
                  className="bg-transparent py-5 px-2 fw-bold w-100"
                  type="text"
                  inputMode="decimal"
                  id="withdraw-amount"
                  name="amount"
                  placeholder="0.00"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmountFn(e.target.value)}
                  onBlur={formatWithdrawAmountOnBlur}
                  required
                />
                <span className="fw-bold" aria-hidden="true">
                  CAD
                </span>
              </div>
            </div>

            <p className="fs-seven mb-6">
              Your withdrawal will be sent by Interac e-Transfer to the
              registered email above. The recipient name and email must match
              your Interac account, otherwise the transfer cannot be completed.
            </p>

            {/* Withdrawal eligibility requirements */}
            <div className="p1-bg rounded-3 p-4 mb-6">
              <h6 className="n10-color mb-3">Withdrawal requirements</h6>
              <p
                className="fs-seven mb-2"
                style={{ color: meetsTradeCount ? "#22c55e" : "#ef4444" }}
              >
                <i
                  className={`ti ${meetsTradeCount ? "ti-circle-check" : "ti-circle-x"} me-2`}
                ></i>
                At least {MIN_TRADES} trades placed ({tradeCount}/{MIN_TRADES})
              </p>
              <p
                className="fs-seven mb-0"
                style={{ color: meetsTradedAmount ? "#22c55e" : "#ef4444" }}
              >
                <i
                  className={`ti ${meetsTradedAmount ? "ti-circle-check" : "ti-circle-x"} me-2`}
                ></i>
                At least ${MIN_TRADED_AMOUNT} of total trades ($
                {totalTraded.toFixed(2)}/${MIN_TRADED_AMOUNT})
              </p>
            </div>

            {/* Terms and Conditions */}
            <div className="mb-5">
              <label htmlFor="terms" className="terms-label">
                <input
                  type="checkbox"
                  id="terms-checkbox-withdraw"
                  name="terms"
                  required
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  style={{ marginRight: "8px" }}
                />
                I fully read and agree to the following{" "}
                <button
                  type="button"
                  className="g1-color"
                  onClick={() => setShowModalWithdrawTerms(true)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    margin: 0,
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  Terms and Conditions
                </button>
              </label>
            </div>

            {/* Submit Button */}
            <div>
              <button
                className="cmn-btn px-5 py-3 mb-6 w-100"
                id="withdraw-submit-button"
                type="submit"
                disabled={!meetsWithdrawRequirements || isSubmittingWithdrawal}
              >
                Withdraw {formatCadAmount(numericAmount)}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
